import React, { FC } from 'react'

import { useWindowSize } from '@smarty-nx/code-utils'
import { getFooter } from '@smarty-nx/contentful'
import { getSmartyVAT } from '@smarty-nx/data'
import { breakpoints, Typography } from '@smarty-nx/ds'
import { TRACKING_CATEGORIES } from '@smarty-nx/tracking'
import cn from 'classnames'
import { Link } from 'gatsby'

import classes from './footer.module.scss'
import googlePlayStore from './google-play.webp'
import iosApp from './ios-app.webp'
import { CONFIG } from '../../config'
import { useIsAuthenticated } from '../../hooks'
import { APP, useGetUserApp } from '../../hooks/use-get-user-app'

export const Footer: FC = () => {
  const isAuthenticated = useIsAuthenticated()
  const userApp = useGetUserApp()
  const footerData = getFooter(
    'marketing',
    isAuthenticated ? 'Authenticated' : 'Unauthenticated'
  )
  const { width } = useWindowSize()
  const isMobileView = Boolean(width && width < breakpoints.tablet)
  return (
    <footer
      role="contentinfo"
      className={cn(classes.footerContainer, classes.redesignContainer)}
      data-testid="footer-container"
      data-module-name="footer"
    >
      <div className={classes.footerInner}>
        {footerData?.footerGroups?.map((footerGroup, i) => {
          return (
            <ul className={classes.footerColumn} key={i}>
              <li className={classes.footerItem}>
                <Typography
                  variant="Kollektif/ExtraSmallEmphasis"
                  variantLg="Kollektif/SmallEmphasis"
                  className={classes.footerColumnTitle}
                >
                  {footerGroup.title}
                </Typography>
              </li>
              {footerGroup.footerItems.map((footerItem, j) => {
                const attrs = {
                  target: footerItem.target,
                  rel: footerItem.rel,
                  'data-testid': footerItem.testId,
                  'data-item-category': TRACKING_CATEGORIES.NAVIGATION,
                  'data-item-name': footerItem.text,
                }
                return (
                  <li className={classes.footerItem} key={j}>
                    {footerItem.isRouterLink ? (
                      <Link to={footerItem.href} {...attrs}>
                        {footerItem.text}
                      </Link>
                    ) : (
                      <a href={footerItem.href} {...attrs}>
                        {footerItem.text}
                      </a>
                    )}
                  </li>
                )
              })}
            </ul>
          )
        })}
        {!isMobileView && (
          <ul className={cn(classes.footerColumn, classes.withActions)}>
            <li className={classes.footerItem}>
              {!isAuthenticated ? (
                <div className={classes.footerActions}>
                  <a
                    className={classes.footerButton}
                    href={`${
                      CONFIG.SMARTY_ENV === 'localhost'
                        ? CONFIG.DASHBOARD_URL
                        : '/'
                    }login`}
                    data-item-category={TRACKING_CATEGORIES.NAVIGATION}
                    data-item-name="log-in"
                    data-testid="footer-login-button"
                  >
                    Log in
                  </a>
                  <Typography component="span" variant="Kollektif/ExtraSmall">
                    Got a SIM?
                  </Typography>
                  <a
                    href="/activate"
                    data-testid="activate-sim-button"
                    data-item-name="activate-sim"
                    data-item-category={TRACKING_CATEGORIES.NAVIGATION}
                  >
                    Activate SIM
                  </a>
                  <button
                    id="ot-sdk-btn"
                    className="ot-sdk-show-settings cookie-pref-btn"
                  >
                    Cookie preferences
                  </button>
                </div>
              ) : (
                <div className={classes.footerActions}>
                  <a
                    className={classes.footerButton}
                    href={`${
                      userApp === APP.DASHBOARD
                        ? CONFIG.DASHBOARD_URL
                        : CONFIG.FAMILY_URL
                    }`}
                    data-item-category={TRACKING_CATEGORIES.NAVIGATION}
                    data-item-name="go-to-dashboard"
                    data-testid="footer-dashboard-button"
                  >
                    Dashboard
                  </a>
                  <span>
                    <a
                      href="/activate"
                      data-testid="activate-sim-button"
                      data-item-name="activate-sim"
                      data-item-category={TRACKING_CATEGORIES.NAVIGATION}
                    >
                      Activate SIM
                    </a>
                  </span>
                  <button
                    id="ot-sdk-btn"
                    className="ot-sdk-show-settings cookie-pref-btn"
                  >
                    Cookie preferences
                  </button>
                </div>
              )}
            </li>
            <li className={classes.footerItem}>
              <a
                href="https://apps.apple.com/app/apple-store/id1540812668?pt=421745&mt=8&ct=smarty_web_download"
                rel="noopener noreferrer"
                target="_blank"
                aria-label="iOS App"
                data-testid="footer-ios-app-download-link"
                data-item-name="ios-app-download-link"
                data-item-category={TRACKING_CATEGORIES.NAVIGATION}
              >
                <img
                  className={classes.iosAppImage}
                  alt="Ios App"
                  data-testid="ios-app"
                  src={iosApp}
                />
              </a>
            </li>
            <li className={classes.footerItem}>
              <a
                href="https://play.google.com/store/apps/details?id=com.hutchison3g.sundae&referrer=utm_source%3Dsmarty%26utm_medium%3Dweb%26utm_content%3Dappstore_logo"
                rel="noopener noreferrer"
                target="_blank"
                aria-label="Google play store App"
                data-testid="footer-android-app-download-link"
                data-item-name="android-app-download-link"
                data-item-category={TRACKING_CATEGORIES.NAVIGATION}
              >
                <img
                  className={classes.googlePlayStoreImage}
                  alt="Google Play Store App"
                  data-testid="google-play-store-app"
                  src={googlePlayStore}
                />
              </a>
            </li>
          </ul>
        )}
      </div>
      {isMobileView && (
        <div className={classes.footerBlockActions}>
          <div>
            {!isAuthenticated ? (
              <div className={classes.footerActions}>
                <a
                  className={classes.footerButton}
                  href={`${
                    CONFIG.SMARTY_ENV === 'localhost'
                      ? CONFIG.DASHBOARD_URL
                      : '/'
                  }login`}
                  data-item-category={TRACKING_CATEGORIES.NAVIGATION}
                  data-item-name="log-in"
                  data-testid="footer-login-button"
                >
                  Log in
                </a>
                <Typography component="span" variant="Kollektif/ExtraSmall">
                  Got a SIM?
                </Typography>
                <a
                  href="/activate"
                  data-testid="activate-sim-button"
                  data-item-name="activate-sim"
                  data-item-category={TRACKING_CATEGORIES.NAVIGATION}
                >
                  Activate SIM
                </a>
                <button
                  id="ot-sdk-btn"
                  className="ot-sdk-show-settings cookie-pref-btn"
                >
                  Cookie preferences
                </button>
              </div>
            ) : (
              <div className={classes.footerActions}>
                <a
                  className={classes.footerButton}
                  href={`${CONFIG.DASHBOARD_URL}`}
                  data-item-category={TRACKING_CATEGORIES.NAVIGATION}
                  data-item-name="go-to-dashboard"
                  data-testid="footer-dashboard-button"
                >
                  Go to my dashboard
                </a>
                <span>
                  <a
                    href="/activate"
                    data-testid="activate-sim-button"
                    data-item-name="activate-sim"
                    data-item-category={TRACKING_CATEGORIES.NAVIGATION}
                  >
                    Activate SIM
                  </a>
                </span>
                <button
                  id="ot-sdk-btn"
                  className="ot-sdk-show-settings cookie-pref-btn"
                >
                  Cookie preferences
                </button>
              </div>
            )}
          </div>
          <div className={classes.apps}>
            <a
              href="https://apps.apple.com/app/apple-store/id1540812668?pt=421745&mt=8&ct=smarty_web_download"
              rel="noopener noreferrer"
              target="_blank"
              aria-label="iOS App"
              data-testid="footer-ios-app-download-link"
              data-item-name="ios-app-download-link"
              data-item-category={TRACKING_CATEGORIES.NAVIGATION}
            >
              <img
                className={classes.iosAppImage}
                alt="Ios App"
                data-testid="ios-app"
                src={iosApp}
              />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.hutchison3g.sundae&referrer=utm_source%3Dsmarty%26utm_medium%3Dweb%26utm_content%3Dappstore_logo"
              rel="noopener noreferrer"
              target="_blank"
              aria-label="Google play store App"
              data-testid="footer-android-app-download-link"
              data-item-name="android-app-download-link"
              data-item-category={TRACKING_CATEGORIES.NAVIGATION}
            >
              <img
                className={classes.googlePlayStoreImage}
                alt="Google Play Store App"
                data-testid="google-play-store-app"
                src={googlePlayStore}
              />
            </a>
          </div>
        </div>
      )}
      <div className={classes.footerSocial}>
        <Typography
          component="span"
          variant="Kollektif/ExtraSmall"
          dataTestId="footer-social-span"
        >
          Follow SMARTY
        </Typography>
        <a
          href="https://www.facebook.com/SMARTYMobileuk"
          rel="noopener noreferrer"
          target="_blank"
          id="facebook"
          data-testid="footer-facebook-icon"
        >
          Facebook
        </a>
        <a
          href="https://x.com/SMARTYMobileUK"
          rel="noopener noreferrer"
          target="_blank"
          id="x"
          data-testid="footer-x-icon"
        >
          X
        </a>
        <a
          href="https://www.youtube.com/@SMARTYMobileUK"
          rel="noopener noreferrer"
          target="_blank"
          id="youtube"
          data-testid="footer-youtube-icon"
        >
          YouTube
        </a>
        <a
          href="https://www.linkedin.com/company/smartymobile-uk/"
          rel="noopener noreferrer"
          target="_blank"
          id="linkedin"
          data-testid="footer-linkedin-icon"
        >
          LinkedIn
        </a>
        <a
          href="https://www.instagram.com/smartymobileuk/"
          rel="noopener noreferrer"
          target="_blank"
          id="instagram"
          data-testid="footer-instagram-icon"
        >
          Instagram
        </a>
      </div>
      <div className={classes.footerTerms}>
        <Typography
          variant="Kollektif/ExtraSmall"
          dataTestId="footer-copyright"
        >
          © {new Date().getFullYear()} All rights reserved. SMARTY.
        </Typography>
        <Typography variant="Kollektif/ExtraSmall">
          <a href="/" data-testid="footer-self-link">
            smarty.co.uk
          </a>
        </Typography>
        <Typography
          variant="Kollektif/ExtraSmall"
          dataTestId="footer-personal-use"
        >
          SMARTY is a service that’s just for consumers and is only intended for
          personal use.
        </Typography>
        <Typography variant="Kollektif/ExtraSmall" dataTestId="footer-trading">
          SMARTY is a trading name of Hutchison 3G UK Limited
        </Typography>
        <div>
          <Typography
            variant="Kollektif/ExtraSmall"
            dataTestId="footer-address"
          >
            Registered address: Vodafone House, The Connection, Newbury,
            Berkshire, RG14 2FN
          </Typography>

          <Typography
            variant="Kollektif/ExtraSmall"
            dataTestId="footer-comp-num"
          >
            Registered in England & Wales | Registered company number: 03885486
          </Typography>
          <Typography variant="Kollektif/ExtraSmall" dataTestId="footer-vat">
            VAT Registration Number {getSmartyVAT()}
          </Typography>
        </div>
      </div>
    </footer>
  )
}

export default Footer
